import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from "@mui/material";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import { Add, Edit, Delete, Refresh, Visibility } from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";
// import ErrorBoundary from "../../../components/ErrorBoundary";

export default function SchoolAdminsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [hasPermission, setHasPermission] = useState(false);

  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState({ main: true, schools: true });
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  // For filtering
  const [selectedSchool, setSelectedSchool] = useState("");
  // For dialog multi-select
  const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [adminToImpersonate, setAdminToImpersonate] = useState(null);

  // Memoize loadData and loadSchools to avoid lint errors
  const loadData = React.useCallback(async (schoolId = "") => {
    if (!hasPermission) return;
    setLoading((prev) => ({ ...prev, main: true }));
    try {
      const url = schoolId 
        ? `/api/school-admins?schoolId=${schoolId}`
        : "/api/school-admins";
      const data = await fetchJson(url);
      setSchoolAdmins(data);
    } catch (e) {
      setError(t("schoolAdmins.errors.load") || "Failed to load school admins");
    } finally {
      setLoading(prev => ({ ...prev, main: false }));
    }
  }, [t, hasPermission]);

  const loadSchools = React.useCallback(async () => {
    if (!hasPermission) return;
    setLoading(prev => ({ ...prev, schools: true }));
    try {
      const data = await fetchJson("/schools");
      setSchools(data.schools || []);
    } catch (e) {
      setError(t("schoolAdmins.errors.schools") || "Failed to load schools");
    } finally {
      setLoading(prev => ({ ...prev, schools: false }));
    }
  }, [t, hasPermission]);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!user) {
        setHasPermission(false);
        return;
      }

      const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
      const allowed = user.role === 'SystemAdmin' || !perms || perms.includes('*') || perms.includes('users-access');
      setHasPermission(allowed);
    };

    checkPermissions();
  }, [user]);

  const canFeature = React.useCallback((permKey) => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes(permKey);
  }, [user]);

  const canManageUsersAccess = canFeature('users-access');
  const canCreate = canManageUsersAccess;
  const canEdit = canManageUsersAccess;
  const canDelete = canManageUsersAccess;

  useEffect(() => {
    if (hasPermission) {
      loadData();
    }
  }, [loadData, hasPermission]);

  useEffect(() => {
    if (hasPermission) {
      loadSchools();
    }
  }, [loadSchools, hasPermission]);

  const handleEdit = (admin) => {
    setCurrentAdmin(admin);
    // admin.schools is an array of school objects
    setSelectedSchoolIds(Array.isArray(admin.schools) ? admin.schools.map(s => s.id) : []);
    setOpenDialog(true);
  };

  const handleCreate = () => {
    setCurrentAdmin({
      name: "",
      email: "",
      status: "active"
    });
    setSelectedSchoolIds([]);
    setOpenDialog(true);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const handleDelete = (id) => {
    const admin = schoolAdmins.find((a) => a.id === id);
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;
    try {
      await fetchJson(`/api/school-admins/${adminToDelete.id}`, {
        method: "DELETE",
      });
      loadData();
    } catch (e) {
      // Show backend error if available
      let msg = t("schoolAdmins.errors.delete") || "Failed to delete admin";
      if (e && e.error) {
        msg += `: ${e.error}`;
      }
      setError(msg);
    } finally {
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleImpersonate = (admin) => {
    setAdminToImpersonate(admin);
    setImpersonateDialog(true);
  };

  const confirmImpersonate = async () => {
    if (!canEdit) return;
    try {
      const data = await fetchJson(`/api/school-admins/${adminToImpersonate.id}/impersonate`, {
        method: "POST",
      });
      
      const alreadyImpersonating = !!localStorage.getItem('impersonationToken');
      const currentAccessToken = localStorage.getItem('accessToken');
      if (!alreadyImpersonating && currentAccessToken && !localStorage.getItem('impersonatorAccessToken')) {
        localStorage.setItem('impersonatorAccessToken', currentAccessToken);
      }

      const prevActiveSchoolId = localStorage.getItem('activeSchoolId') || '';
      const prevScopeSchoolId = localStorage.getItem('scopeSchoolId') || '';
      if (!localStorage.getItem('impersonatorActiveSchoolId')) {
        localStorage.setItem('impersonatorActiveSchoolId', prevActiveSchoolId);
      }
      if (!localStorage.getItem('impersonatorScopeSchoolId')) {
        localStorage.setItem('impersonatorScopeSchoolId', prevScopeSchoolId);
      }

      const selectedSchoolId =
        (adminToImpersonate && adminToImpersonate.schoolId != null) ? String(adminToImpersonate.schoolId)
        : (Array.isArray(adminToImpersonate?.schools) && adminToImpersonate.schools.length) ? String(adminToImpersonate.schools[0].id)
        : '';
      if (selectedSchoolId) {
        localStorage.setItem('activeSchoolId', selectedSchoolId);
        localStorage.setItem('scopeSchoolId', selectedSchoolId);
      }
      localStorage.removeItem('activeScopeLevel');
      localStorage.removeItem('scopeLevel');

      localStorage.setItem('impersonationToken', data.token);
      try { localStorage.removeItem('user'); } catch (_) {}
      try { window.dispatchEvent(new Event('app:auth:changed')); } catch (_) {}
      window.location.href = '/school-overview';
      
    } catch (e) {
      setError(t("schoolAdmins.errors.impersonate") || "Failed to impersonate admin");
    } finally {
      setImpersonateDialog(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...currentAdmin,
        schoolIds: selectedSchoolIds // send array of school IDs
      };
      if (currentAdmin.id) {
        await fetchJson(`/api/school-admins/${currentAdmin.id}`, {
          method: "PUT",
          body: JSON.stringify(data)
        });
      } else {
        await fetchJson("/api/school-admins", {
          method: "POST",
          body: JSON.stringify(data)
        });
      }
      setOpenDialog(false);
      loadData();
    } catch (e) {
      setError(t("schoolAdmins.errors.save") || "Failed to save admin");
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: t("schoolAdmins.columns.name") || "Name",
      flex: 1,
      renderCell: (params) => (
        params?.row ? (
          <Stack>
            <Typography fontWeight="medium">{params.value}</Typography>
            <Typography variant="body2" color="textSecondary">
              {params.row.email}
            </Typography>
          </Stack>
        ) : null
      )
    },
    {
      field: 'schools',
      headerName: t("schoolAdmins.columns.school") || "School(s)",
      flex: 2,
      renderCell: (params) => {
        if (Array.isArray(params?.row?.schools) && params.row.schools.length > 0) {
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {params.row.schools.map(school => (
                <Chip
                  key={school.id}
                  label={school.name + (school.status ? ` (${school.status})` : '')}
                  size="small"
                  color={school.status === 'active' ? 'success' : (school.status === 'suspended' ? 'warning' : 'default')}
                />
              ))}
            </Stack>
          );
        }
        return <Typography variant="body2" color="textSecondary">N/A</Typography>;
      }
    },
    {
      field: 'status',
      headerName: t("schoolAdmins.columns.status") || "Status",
      width: 120,
      renderCell: (params) => (
        params?.row ? (
          <Chip
            label={params.row.status}
            color={params.row.status === 'active' ? 'success' : (params.row.status === 'suspended' ? 'warning' : 'default')}
            variant="outlined"
            size="small"
          />
        ) : null
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t("schoolAdmins.columns.actions") || "Actions",
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label={t("common.edit") || "Edit "}
          onClick={() => handleEdit(params?.row)}
          disabled={!canEdit}
        />,
        <GridActionsCellItem
          icon={<Visibility />}
          label={t("schoolAdmins.actions.impersonate") || "Impersonate"}
          onClick={() => handleImpersonate(params.row)}
          disabled={params.row.status !== 'active' || !canEdit}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label={t("common.delete") || "Delete"}
          onClick={() => handleDelete(params.id)}
          showInMenu
          disabled={!canDelete}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      {!hasPermission ? (
        <Box p={3}>
          <Alert 
            severity="error" 
            icon={<LockIcon />}
            action={
              <Typography variant="body2" color="text.secondary">
                Contact your administrator for access
              </Typography>
            }
          >
            Access Denied: You do not have Super Admin permissions to manage School Admins.
          </Alert>
        </Box>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
          mb={3}
        >
          <Typography variant="h4">
            {t("superAdmin.schoolAdmins.title") || "School Admins"}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            disabled={loading.main || !canCreate}
          >
            {t("superAdmin.schoolAdmins.addNew") || "Add School Admin"}
          </Button>
        </Stack>
      )}
      {hasPermission && (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={3}>
          <Typography variant="h4">
            {t("schoolAdmins.title") || "School Admins"}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Select
              value={selectedSchool}
              onChange={(e) => {
                setSelectedSchool(e.target.value);
                loadData(e.target.value);
              }}
              displayEmpty
              sx={{ minWidth: 200 }}
              disabled={loading.schools}
            >
              <MenuItem value="">
                {t("schoolAdmins.filter.allSchools") || "All Schools"}
              </MenuItem>
              {schools.map(school => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name}
                </MenuItem>
              ))}
            </Select>
            <Button 
              variant="contained" 
              startIcon={<Add />}
              onClick={handleCreate}
              disabled={loading.main || !canCreate}
            >
              {t("schoolAdmins.addButton") || "Add School Admin"}
            </Button>
          </Stack>
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {hasPermission && (
        <Card>
          <CardContent>
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={schoolAdmins}
                columns={columns}
                loading={loading.main}
                components={{ Toolbar: GridToolbar }}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                localeText={{
                  toolbarDensity: t("datagrid.density"),
                  // ... other localization keys
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {currentAdmin?.id 
            ? t("schoolAdmins.editTitle") || "Edit School Admin" 
            : t("schoolAdmins.createTitle") || "Create School Admin"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t("schoolAdmins.form.name") || "Name"}
              value={currentAdmin?.name || ""}
              onChange={(e) => setCurrentAdmin({...currentAdmin, name: e.target.value})}
            />
            <TextField
              fullWidth
              label={t("schoolAdmins.form.email") || "Email"}
              type="email"
              value={currentAdmin?.email || ""}
              onChange={(e) => setCurrentAdmin({...currentAdmin, email: e.target.value})}
            />
            <FormControl fullWidth>
              <InputLabel>{t("schoolAdmins.form.school") || "School(s)"}</InputLabel>
              <Select
                multiple
                value={selectedSchoolIds}
                onChange={(e) => setSelectedSchoolIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                label={t("schoolAdmins.form.school") || "School(s)"}
                renderValue={(selected) =>
                  schools
                    .filter(s => selected.includes(s.id))
                    .map(s => s.name)
                    .join(', ')
                }
              >
                {schools.map(school => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t("schoolAdmins.form.status") || "Status"}</InputLabel>
              <Select
                value={currentAdmin?.status || "active"}
                onChange={(e) => setCurrentAdmin({...currentAdmin, status: e.target.value})}
                label={t("schoolAdmins.form.status") || "Status"}
              >
                <MenuItem value="active">{t("common.status.active") || "Active"}</MenuItem>
                <MenuItem value="inactive">{t("common.status.inactive") || "Inactive"}</MenuItem>
                <MenuItem value="suspended">{t("common.status.suspended") || "Suspended"}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!(currentAdmin?.id ? canEdit : canCreate)}>
            {t("common.save") || "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impersonation Dialog */}
      <Dialog 
        open={impersonateDialog} 
        onClose={() => setImpersonateDialog(false)}
      >
        <DialogTitle>
          {t("schoolAdmins.impersonateTitle") || "Confirm Impersonation"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t("schoolAdmins.impersonateMessage") || "You are about to impersonate:"}
          </Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {adminToImpersonate?.name}
          </Typography>
          <Typography color="textSecondary">
            {adminToImpersonate?.email}
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            {t("schoolAdmins.impersonateWarning") || "All actions will be recorded under this user's account."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpersonateDialog(false)}>
            {t("common.cancel") || "Cancel"}
          </Button>
          <Button onClick={confirmImpersonate} variant="contained" color="warning" disabled={!canEdit}>
            {t("schoolAdmins.impersonateButton") || "Confirm Impersonation"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setAdminToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t("schoolAdmins.confirmDeleteTitle") || "Delete Admin"}
        message={t("schoolAdmins.confirmDelete") || "Are you sure you want to delete this admin?"}
        confirmText={t("common.delete") || "Delete"}
        cancelText={t("common.cancel") || "Cancel"}
      />
    </Box>
  );
}